import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

/**
 * Variant note on the brand teal: `primary` fills with `brand-fill` and labels it with
 * `on-brand-fill` ink (6.0:1). White on the raw teal would be 2.8:1 and fail AA, which is why no
 * variant here pairs them. See src/styles/theme.css.
 *
 * Below `md` every button is given a 44px floor. It is a floor rather than a height, so a button
 * that is already larger is untouched, and it is scoped to the narrow layouts so the desktop
 * stays exactly as dense as it is — that density is deliberate, and it is worked by someone
 * looking at the screen all day rather than tapping it with a thumb.
 */
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 max-md:min-h-11 max-md:min-w-11 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-brand-fill text-on-brand-fill hover:bg-brand-fill-hover active:bg-brand-fill-active',
        secondary:
          'border border-border-subtle bg-surface text-heading hover:bg-surface-hover hover:border-border-strong',
        ghost: 'text-body hover:bg-surface-hover hover:text-heading',
        subtle: 'bg-brand-fill-subtle text-brand-text hover:bg-brand-fill-subtle-hover',
        danger: 'bg-danger-fill text-on-danger-fill hover:bg-danger-fill/90',
        link: 'text-brand-text underline underline-offset-4 hover:text-brand-text-hover',
      },
      size: {
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-9 px-4 text-sm [&_svg]:size-4',
        lg: 'h-10 px-5 text-sm [&_svg]:size-4',
        icon: 'size-9 [&_svg]:size-4',
        'icon-sm': 'size-8 [&_svg]:size-3.5',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  },
)

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Component = asChild ? Slot : 'button'
  return <Component className={cn(buttonVariants({ variant, size }), className)} {...props} />
}

export { buttonVariants }
